import { ExternalLinkIcon, MenuIcon, ScrollTextIcon, ShieldIcon, TvMinimalPlayIcon } from 'lucide-react'
import { useState } from 'react'

import { BaseMiniAppOptions } from '../types'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIcon,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { MenuItem, MenuItemIcon } from '@/ui/atoms/menu-item/MenuItem'
import { links } from '@/ui/constants/links'
import { BuildInfo } from '@/ui/utils/getBuildInfo'

export interface TopbarMenuProps {
  isMobileDisplay: boolean
  buildInfo: BuildInfo
  baseMiniAppOptions: BaseMiniAppOptions | undefined
}

export function MiniappTopbarMenu({ buildInfo, isMobileDisplay, baseMiniAppOptions }: TopbarMenuProps) {
  const [open, setOpen] = useState(false)

  const { sha = 'n/a', buildTime = 'n/a' } = buildInfo
  const triggerButton = <IconButton icon={MenuIcon} variant="tertiary" size="m" />

  if (isMobileDisplay) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        <DialogContent overlayVariant="default" contentVerticalPosition="bottom" className="p-0">
          <DialogTitle className="border-primary border-b p-5 pt-6">Menu</DialogTitle>

          {baseMiniAppOptions && (
            <MenuItem
              asChild
              variant="secondary"
              className="cursor-pointer rounded-none p-6"
              withSeparator
              onClick={() => {
                setOpen(false)
                baseMiniAppOptions.openTutorialDialog()
              }}
            >
              <div className="flex w-full items-center gap-2">
                <MenuItemIcon icon={TvMinimalPlayIcon} />
                How to deposit?
              </div>
            </MenuItem>
          )}

          <MenuItem asChild variant="secondary" className="cursor-pointer rounded-none p-6" withSeparator>
            <ExternalLink to={links.sparkExternal.termsOfUse} variant="unstyled">
              <MenuItemIcon icon={ScrollTextIcon} />
              Terms of Service
              <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
            </ExternalLink>
          </MenuItem>

          <MenuItem asChild variant="secondary" className="cursor-pointer rounded-none p-6" withSeparator>
            <ExternalLink to={links.sparkExternal.privacyPolicy} variant="unstyled">
              <MenuItemIcon icon={ShieldIcon} />
              Privacy Policy
              <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
            </ExternalLink>
          </MenuItem>

          <span className="typography-label-4 bg-secondary p-6 text-center text-secondary">
            Built from {sha} at {buildTime}
          </span>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" variant="secondary" className="flex w-80 flex-col gap-1.5 p-1">
        {baseMiniAppOptions && (
          <DropdownMenuItem
            asChild
            variant="secondary"
            className="flex cursor-pointer items-start py-5"
            onSelect={baseMiniAppOptions.openTutorialDialog}
          >
            <div>
              <DropdownMenuItemIcon icon={TvMinimalPlayIcon} />
              How to deposit?
            </div>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.sparkExternal.termsOfUse} variant="unstyled">
            <DropdownMenuItemIcon icon={ScrollTextIcon} />
            Terms of Service
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.sparkExternal.privacyPolicy} variant="unstyled">
            <DropdownMenuItemIcon icon={ShieldIcon} />
            Privacy Policy
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <span className="typography-label-4 p-5 text-center text-secondary">
          Built from {sha} at {buildTime}
        </span>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

MiniappTopbarMenu.displayName = 'TopbarMenu'
